import { useState } from "react";
import { formatCount, formatDate, themeOf, type Article } from "@/utils/content";

export default function ArticleBody({ article }: { article: Article }) {
  const [copied, setCopied] = useState(false);
  const theme = themeOf(article.category);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <article className="min-w-0">
      <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
        <div className="relative h-56 w-full md:h-72">
          <img
            src={article.cover}
            alt={article.title}
            title={`${article.title} - 511爆料`}
            className="h-full w-full object-top object-cover"
          />
        </div>

        <div className="p-6 md:p-8">
          <div className="flex flex-wrap items-center gap-3 text-xs text-foreground-500">
            <span className={`inline-flex items-center gap-1 rounded px-2 py-0.5 font-semibold ${theme.chip}`}>
              {article.categoryName}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-user-3-line" />
              {article.author} · {article.authorRole}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-calendar-line" />
              {formatDate(article.date)}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-time-line" />
              约 {article.readTime}
            </span>
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-4 text-sm text-foreground-600">
            <span className="flex items-center gap-1">
              <i className="ri-eye-line" />
              {formatCount(article.views)} 阅读
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-heart-3-line" />
              {formatCount(article.likes)} 收藏
            </span>
            <button
              type="button"
              onClick={handleCopy}
              className="ml-auto flex items-center gap-1 rounded-md border border-background-300 px-3 py-1.5 text-xs font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
            >
              <i className={copied ? "ri-check-line" : "ri-link"} />
              {copied ? "链接已复制" : "复制链接"}
            </button>
          </div>

          <div className={`mt-8 rounded-lg border p-5 ${theme.border} ${theme.soft}`}>
            <h2 className="flex items-center gap-2 font-heading text-sm font-bold text-foreground-950">
              <i className="ri-list-check-2" />
              要点速览
            </h2>
            <ul className="mt-3 flex flex-col gap-2.5">
              {article.points.map((point, index) => (
                <li key={point} className="flex items-start gap-2 text-sm leading-relaxed text-foreground-700">
                  <span className={`mt-1 flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[10px] font-bold ${theme.icon}`}>
                    {index + 1}
                  </span>
                  {point}
                </li>
              ))}
            </ul>
          </div>

          <div className="prose-cn mt-8 text-[15px] text-foreground-800">
            {article.content.map((block, index) => {
              if (block.type === "h2") {
                return (
                  <h2
                    key={`${article.id}-${index}`}
                    id={`section-${index}`}
                    className="mb-4 mt-10 scroll-mt-24 font-heading text-lg font-bold text-foreground-950 md:text-xl"
                  >
                    {block.text}
                  </h2>
                );
              }
              if (block.type === "quote") {
                return (
                  <blockquote
                    key={`${article.id}-${index}`}
                    className="my-6 border-l-4 border-primary-400 bg-background-100 px-5 py-4 font-heading text-base italic leading-relaxed text-foreground-800"
                  >
                    {block.text}
                  </blockquote>
                );
              }
              return (
                <p key={`${article.id}-${index}`} className="text-foreground-700">
                  {block.text}
                </p>
              );
            })}
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-2 border-t border-background-200 pt-6">
            <span className="text-xs font-semibold text-foreground-500">相关标签</span>
            {article.tags.map((tag) => (
              <span key={tag} className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900">
                {tag}
              </span>
            ))}
          </div>
        </div>
      </div>
    </article>
  );
}